// ────────────────────────────────────────────────────────────────────────────
//  SickLeaves.tsx — Krankmeldungen einreichen + verwalten
//
//  Zwei Tabs:
//    • Meine        — eigene Krankmeldungen + „Neu einreichen"
//    • Inbox        — Manager-Sicht (gated auf canApproveAbsences)
//
//  Auf Mitglied-Accounts ohne Approval-Permission ist der Inbox-Tab
//  ausgeblendet — der Server gated zusätzlich.
// ────────────────────────────────────────────────────────────────────────────

import { useState, useMemo } from 'react'
import { useAuth } from '../lib/useAuth'
import {
  useMySickLeaves, useSickLeaveInbox,
  submitSickLeave, uploadSickLeaveAttachment,
  approveSickLeave, followupSickLeave, rejectSickLeave, fileSickLeaveToRecord,
  STATUS_META,
  type SickLeave, type SickLeaveStatus,
} from '../lib/useSickLeaves'
import { API_ORIGIN } from '../lib/api'

type Tab = 'mine' | 'inbox'

export function SickLeaves() {
  const { user } = useAuth()
  // Owner / Admin / `canApproveAbsences`-Permission. Webapp-AppUser-Schema ist
  // schmaler als das iOS-Modell — `myOrgPermissions` und `isOrgOwner` sind
  // nicht typisiert; wir prüfen via Cast. Server gated zusätzlich.
  const canApprove = useMemo(() => {
    if (!user) return false
    const u = user as any
    if (u.orgRole === 'Owner' || u.orgRole === 'Admin') return true
    return u.myOrgPermissions?.canApproveAbsences === true
  }, [user])

  const [tab, setTab] = useState<Tab>('mine')

  if (!user) return null

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 md:py-10 space-y-6">
      <header>
        <div className="text-[10px] font-bold tracking-widest text-app-text-mute mb-1">
          ABWESENHEITEN
        </div>
        <h1 className="text-2xl md:text-3xl font-bold">Krankmeldungen</h1>
        <p className="text-app-text-dim text-sm mt-1">
          Reiche Krankmeldungen mit Attest direkt ein — Manager bekommen sie sofort
          zur Bestätigung in ihre Inbox.
        </p>
      </header>

      {/* Tabs */}
      <nav className="flex flex-wrap gap-2">
        <TabButton active={tab === 'mine'}  onClick={() => setTab('mine')}>
          Meine Krankmeldungen
        </TabButton>
        {canApprove && (
          <TabButton active={tab === 'inbox'} onClick={() => setTab('inbox')}>
            Manager-Inbox
          </TabButton>
        )}
      </nav>

      {tab === 'mine'  ? <MineTab />  : null}
      {tab === 'inbox' ? <InboxTab /> : null}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  Mine — eigene Krankmeldungen + Submit-Form
// ════════════════════════════════════════════════════════════════════════════

function MineTab() {
  const { items, loading, error, reload } = useMySickLeaves()
  const [showSubmit, setShowSubmit] = useState(false)

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-bold">Eingereicht</h2>
        <span className="text-app-text-mute text-xs">{items.length}</span>
        <button
          onClick={() => setShowSubmit(true)}
          className="ml-auto px-4 py-2 rounded-full font-semibold text-sm bg-accent text-white hover:opacity-90 transition"
        >
          + Neu einreichen
        </button>
      </div>

      {loading && <div className="text-sm text-app-text-mute">Lädt…</div>}
      {error && <ErrorBox message={error} onRetry={reload} />}

      {!loading && items.length === 0 && (
        <div className="glass-flat p-8 text-center rounded-2xl">
          <div className="text-4xl mb-2">🩺</div>
          <h3 className="text-base font-bold mb-1">Keine Krankmeldungen</h3>
          <p className="text-app-text-dim text-sm">
            Wenn du krank bist, kannst du hier deine Krankschreibung einreichen.
            Manager bekommen eine Benachrichtigung.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {items.map(s => <SickLeaveCard key={s.id} sl={s} canDecide={false} onChanged={reload} />)}
      </div>

      {showSubmit && (
        <SubmitSheet
          onClose={() => setShowSubmit(false)}
          onSubmitted={() => { setShowSubmit(false); reload() }}
        />
      )}
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  Inbox — Manager-Sicht
// ════════════════════════════════════════════════════════════════════════════

function InboxTab() {
  const [filter, setFilter] = useState<SickLeaveStatus | 'all'>('pending')
  const { items, loading, error, reload } = useSickLeaveInbox(filter)

  const FILTERS: { id: SickLeaveStatus | 'all'; label: string }[] = [
    { id: 'pending',        label: 'Wartend' },
    { id: 'approved',       label: 'Bestätigt' },
    { id: 'needs_followup', label: 'Rückfrage' },
    { id: 'rejected',       label: 'Abgelehnt' },
    { id: 'all',            label: 'Alle' },
  ]

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
              filter === f.id
                ? 'border-accent/60 bg-accent/10 text-app-text'
                : 'border-app-border text-app-text-dim hover:bg-app-elev-2'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-app-text-mute">Lädt…</div>}
      {error && <ErrorBox message={error} onRetry={reload} />}

      {!loading && items.length === 0 && (
        <div className="glass-flat p-8 text-center rounded-2xl">
          <div className="text-4xl mb-2">📭</div>
          <h3 className="text-base font-bold mb-1">Inbox leer</h3>
          <p className="text-app-text-dim text-sm">
            Gerade keine Krankmeldungen mit diesem Status.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {items.map(s => <SickLeaveCard key={s.id} sl={s} canDecide={true} onChanged={reload} />)}
      </div>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  Submit-Sheet — Neuer Eintrag
// ════════════════════════════════════════════════════════════════════════════

function SubmitSheet({ onClose, onSubmitted }: { onClose: () => void; onSubmitted: () => void }) {
  const [hasRange, setHasRange] = useState(false)
  const [startsOn, setStartsOn] = useState(new Date().toISOString().slice(0, 10))
  const [endsOn,   setEndsOn]   = useState(new Date().toISOString().slice(0, 10))
  const [reason,   setReason]   = useState('')
  const [file,     setFile]     = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setSubmitting(true); setError(null)
    try {
      const sick = await submitSickLeave({
        startsOn: hasRange ? startsOn : null,
        endsOn:   hasRange ? endsOn   : null,
        reason:   reason.trim() || undefined,
      })
      if (file) {
        await uploadSickLeaveAttachment(sick.id, file)
      }
      onSubmitted()
    } catch (e: any) {
      setError(e?.message ?? 'Senden fehlgeschlagen.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="glass-strong rounded-3xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <header className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl"
               style={{ background: 'rgba(255,179,0,0.18)' }}>
            🩺
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold">Krankmeldung einreichen</h2>
            <p className="text-xs text-app-text-dim mt-0.5">
              Lade einen Krankschein als PDF oder Foto hoch. Manager bekommen eine Benachrichtigung.
            </p>
          </div>
          <button onClick={onClose} className="text-app-text-mute hover:text-app-text text-xl">×</button>
        </header>

        <div className="rounded-xl border p-3" style={{ borderColor: 'var(--border)' }}>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={hasRange} onChange={e => setHasRange(e.target.checked)} />
            <span className="text-sm font-semibold">Zeitraum angeben</span>
          </label>
          {hasRange && (
            <div className="grid grid-cols-2 gap-2 mt-3">
              <label className="block">
                <div className="text-[10px] font-bold tracking-wider text-app-text-mute mb-1">VON</div>
                <input type="date" value={startsOn} onChange={e => setStartsOn(e.target.value)}
                       className="w-full bg-transparent border rounded-lg px-3 py-2 text-sm"
                       style={{ borderColor: 'var(--border)' }} />
              </label>
              <label className="block">
                <div className="text-[10px] font-bold tracking-wider text-app-text-mute mb-1">BIS</div>
                <input type="date" value={endsOn} min={startsOn} onChange={e => setEndsOn(e.target.value)}
                       className="w-full bg-transparent border rounded-lg px-3 py-2 text-sm"
                       style={{ borderColor: 'var(--border)' }} />
              </label>
            </div>
          )}
        </div>

        <div>
          <div className="text-[10px] font-bold tracking-wider text-app-text-mute mb-1">HINWEIS / GRUND</div>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Optional — z.B. Krankenhaus-Aufenthalt, Arzttermin…"
            rows={4}
            className="w-full bg-transparent border rounded-lg px-3 py-2 text-sm resize-none"
            style={{ borderColor: 'var(--border)' }}
          />
        </div>

        <div>
          <div className="text-[10px] font-bold tracking-wider text-app-text-mute mb-1">ANHANG (PDF / FOTO)</div>
          {file ? (
            <div className="rounded-xl border p-3 flex items-center gap-3"
                 style={{ borderColor: 'var(--border)', background: 'rgba(138,68,238,0.08)' }}>
              <span className="text-2xl">📄</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{file.name}</div>
                <div className="text-[10px] text-app-text-mute">
                  {(file.size / 1024).toFixed(1)} KB
                </div>
              </div>
              <button onClick={() => setFile(null)} className="text-app-text-mute hover:text-app-text text-xl">×</button>
            </div>
          ) : (
            <label className="block w-full rounded-xl border-2 border-dashed py-4 text-center cursor-pointer hover:bg-app-elev-2/30 transition"
                   style={{ borderColor: 'var(--border)' }}>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,application/pdf,image/*"
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0]
                  if (f) {
                    if (f.size > 10 * 1024 * 1024) {
                      setError('Datei zu groß (max 10 MB).')
                      return
                    }
                    setFile(f)
                    setError(null)
                  }
                }}
              />
              <div className="text-sm font-semibold text-accent">📎 Datei wählen</div>
              <div className="text-[10px] text-app-text-mute mt-1">PDF / JPG / PNG / HEIC · max 10 MB</div>
            </label>
          )}
        </div>

        {error && <div className="text-sm text-app-danger">{error}</div>}

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={submitting}
                  className="flex-1 px-4 py-2.5 rounded-full font-semibold text-sm border hover:bg-app-elev-2"
                  style={{ borderColor: 'var(--border)' }}>
            Abbrechen
          </button>
          <button onClick={submit} disabled={submitting}
                  className="flex-1 px-4 py-2.5 rounded-full font-semibold text-sm bg-accent text-white hover:opacity-90 disabled:opacity-60">
            {submitting ? 'Wird gesendet…' : 'Einreichen'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  SickLeave-Card (gemeinsam für Mine und Inbox)
// ════════════════════════════════════════════════════════════════════════════

function SickLeaveCard({
  sl, canDecide, onChanged,
}: {
  sl: SickLeave
  canDecide: boolean
  onChanged: () => void
}) {
  const meta = STATUS_META[sl.status]
  const dateLine = sl.startsOn
    ? (sl.endsOn ? `${sl.startsOn} – ${sl.endsOn}` : sl.startsOn)
    : null

  return (
    <article className="glass-flat rounded-2xl p-4">
      <header className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-base"
             style={{ background: meta.bg, color: meta.color }}>
          {meta.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full"
                  style={{ background: meta.bg, color: meta.color }}>
              {meta.label}
            </span>
            <span className="text-sm font-semibold truncate">
              {sl.submitterName ?? 'Unbekannt'}
            </span>
          </div>
          {dateLine && (
            <div className="text-[11px] font-mono text-app-text-mute mt-0.5">{dateLine}</div>
          )}
        </div>
        <span className="text-[10px] text-app-text-mute shrink-0">
          {new Date(sl.createdAt).toLocaleDateString('de-DE', {
            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
          })}
        </span>
      </header>

      {sl.reason && (
        <p className="text-sm text-app-text-dim mb-3">{sl.reason}</p>
      )}

      {sl.decisionNote && (
        <div className="rounded-lg p-2.5 mb-3 text-xs flex items-start gap-2"
             style={{ background: 'rgba(255,179,0,0.10)', color: '#ffb300' }}>
          <span>💬</span>
          <span>{sl.decisionNote}</span>
        </div>
      )}

      {sl.attachmentUrl && (
        <AttachmentLink sl={sl} />
      )}

      {canDecide && <DecideRow sl={sl} onChanged={onChanged} />}

      {!canDecide && sl.status === 'filed' && (
        <div className="mt-3 text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full font-semibold"
             style={{ background: 'rgba(138,68,238,0.16)', color: '#8a44ee' }}>
          📁 In Mitarbeiter-Akte übernommen
        </div>
      )}
    </article>
  )
}

function AttachmentLink({ sl }: { sl: SickLeave }) {
  const open = () => {
    const token = localStorage.getItem('custosoft.auth.token') ?? ''
    // Auth-protected — wir öffnen die URL via fetch + blob-URL, damit der
    // Browser sie inline rendern kann ohne Cookie-Auth.
    fetch(`${API_ORIGIN}${sl.attachmentUrl}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const blob = await r.blob()
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank', 'noopener,noreferrer')
        // Object-URL nach kurzer Zeit revoken
        setTimeout(() => URL.revokeObjectURL(url), 30_000)
      })
      .catch(err => alert(`Anhang konnte nicht geladen werden: ${err.message}`))
  }
  return (
    <button onClick={open}
            className="w-full text-left rounded-lg p-2.5 mb-3 flex items-center gap-2 text-sm font-semibold border hover:bg-accent/5 transition"
            style={{ borderColor: 'var(--border)', color: 'var(--accent)' }}>
      <span>📎</span>
      <span className="truncate flex-1">{sl.attachmentName ?? 'Anhang öffnen'}</span>
      <span>↗</span>
    </button>
  )
}

function DecideRow({ sl, onChanged }: { sl: SickLeave; onChanged: () => void }) {
  const [busy, setBusy] = useState(false)

  const ask = async (label: string): Promise<string | null> => {
    const note = window.prompt(label, '')
    if (note === null) return null
    return note
  }

  const onApprove = async () => {
    setBusy(true)
    try { await approveSickLeave(sl.id); onChanged() }
    catch (e: any) { alert(e?.message ?? 'Fehler') }
    finally { setBusy(false) }
  }
  const onFollowup = async () => {
    const note = await ask('Was muss der Mitarbeiter klären?')
    if (note === null) return
    setBusy(true)
    try { await followupSickLeave(sl.id, note); onChanged() }
    catch (e: any) { alert(e?.message ?? 'Fehler') }
    finally { setBusy(false) }
  }
  const onReject = async () => {
    const note = await ask('Grund (wird dem Mitarbeiter angezeigt)')
    if (note === null) return
    setBusy(true)
    try { await rejectSickLeave(sl.id, note); onChanged() }
    catch (e: any) { alert(e?.message ?? 'Fehler') }
    finally { setBusy(false) }
  }
  const onFile = async () => {
    if (!confirm('Krankmeldung in die Mitarbeiter-Akte übernehmen?')) return
    setBusy(true)
    try { await fileSickLeaveToRecord(sl.id); onChanged() }
    catch (e: any) { alert(e?.message ?? 'Fehler') }
    finally { setBusy(false) }
  }

  if (sl.status === 'pending') {
    return (
      <div className="grid grid-cols-3 gap-1.5 mt-2">
        <ActionBtn color="#33d670" disabled={busy} onClick={onApprove}>✓ Bestätigen</ActionBtn>
        <ActionBtn color="#3a62ff" disabled={busy} onClick={onFollowup}>? Rückfrage</ActionBtn>
        <ActionBtn color="#e8595d" disabled={busy} onClick={onReject}>✕ Ablehnen</ActionBtn>
      </div>
    )
  }
  if (sl.status === 'approved' && !sl.filedToFileId) {
    return (
      <div className="mt-2">
        <ActionBtn color="#8a44ee" disabled={busy} onClick={onFile}>
          📁 In Akte übernehmen
        </ActionBtn>
      </div>
    )
  }
  if (sl.status === 'filed') {
    return (
      <div className="mt-2 text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full font-semibold"
           style={{ background: 'rgba(138,68,238,0.16)', color: '#8a44ee' }}>
        📁 In Akte übernommen
      </div>
    )
  }
  return null
}

function ActionBtn({
  color, disabled, onClick, children,
}: {
  color: string; disabled?: boolean; onClick: () => void; children: React.ReactNode
}) {
  return (
    <button onClick={onClick} disabled={disabled}
            className="px-3 py-2 rounded-lg text-xs font-semibold border-2 transition disabled:opacity-50 hover:opacity-90"
            style={{ borderColor: color + '55', color, background: color + '14' }}>
      {children}
    </button>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  Helpers
// ════════════════════════════════════════════════════════════════════════════

function TabButton({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
              active
                ? 'border-accent/60 bg-accent/10 text-app-text'
                : 'border-app-border text-app-text-dim hover:bg-app-elev-2'
            }`}>
      {children}
    </button>
  )
}

function ErrorBox({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="glass-flat p-4 rounded-xl flex items-center gap-3">
      <span className="text-app-danger text-xl">⚠️</span>
      <div className="flex-1 text-sm">{message}</div>
      <button onClick={onRetry}
              className="text-xs font-semibold px-3 py-1.5 rounded-full border hover:bg-app-elev-2"
              style={{ borderColor: 'var(--border)' }}>
        Erneut versuchen
      </button>
    </div>
  )
}
